import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FormGroup from '../../../../components/global/FormGroup';
import Button from '../../../../components/global/Button';
import { getFormikTouchedErrors } from '../../../../util/formik';
import Select from '../../../../components/global/Select';
import { PRO_REGISTRATION_PERSONAS } from '../helpers';
import { sortArrayByKey } from '../../../../util/sorting';
import { ACCOUNT_PRO_ROLES } from '../../../../constants/account-roles';
import PhoneInput from '../../../../components/global/PhoneInput';
import getConfig from '../../../../util/config';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  loading: PropTypes.bool,
  requiresCreditCard: PropTypes.bool,
  personaDisabled: PropTypes.bool,
  isWaldoProFreeRegister: PropTypes.bool,
};

const RegisterViewDetailsForm = ({
  className,
  handleSubmit,
  values,
  errors,
  touched,
  loading,
  handleChange,
  handleBlur,
  requiresCreditCard = false,
  personaDisabled = false,
  isWaldoProFreeRegister = false,
}) => {
  const { marcomUrl } = getConfig();
  useEffect(() => {
    if (!values.persona && isWaldoProFreeRegister) {
      handleChange({
        target: {
          name: 'persona',
          value: 'pro',
        },
      });
    }
  }, [isWaldoProFreeRegister]);

  const classes = classNames({
    'view-register-form': true,
    [className]: !!className,
  });
  const errorList = Object.values(
    getFormikTouchedErrors(errors, touched) ?? {},
  );
  const sortedPersonas = sortArrayByKey(PRO_REGISTRATION_PERSONAS, 'label');

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-lg-6 col-12">
          <FormGroup
            id="first_name"
            name="first_name"
            label="First name"
            labelSrOnly
            placeholder="First name"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.first_name}
          />
        </div>
        <div className="col-lg-6 col-12">
          <FormGroup
            id="last_name"
            name="last_name"
            label="Last name"
            labelSrOnly
            placeholder="Last name"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.last_name}
          />
        </div>
        <div className="col-lg-6 col-12">
          <FormGroup
            id="email_address"
            name="email_address"
            label="Email address"
            labelSrOnly
            placeholder="Email address"
            required
            type="email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email_address}
          />
        </div>
        <div className="col-12 col-md-6">
          <PhoneInput
            id="contact_phone"
            autoFocus={false}
            value={values.contact_phone || '+1'}
            onChange={phone => {
              handleChange({
                target: {
                  name: 'contact_phone',
                  value: phone,
                },
              });
            }}
            className="mb-2"
          />
        </div>
        <div className="col-lg-6 col-12">
          <FormGroup
            id="organization_name"
            name="organization_name"
            label="Company name"
            labelSrOnly
            placeholder="Company name"
            required
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.organization_name}
          />
        </div>
        {!personaDisabled && (
          <div className="col-lg-6 col-12">
            <Select
              id="persona"
              name="persona"
              label="Choose the persona that fits best to you"
              className={isWaldoProFreeRegister ? 'mb-0' : ''}
              labelSrOnly
              placeholder="I'm a..."
              required
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.persona}
              readOnly={isWaldoProFreeRegister}
              disabled={personaDisabled || isWaldoProFreeRegister}
            >
              <option value="">I&rsquo;m a&hellip;</option>
              {[
                ...sortedPersonas,
                {
                  label: 'Other',
                  value: 'other',
                  role: ACCOUNT_PRO_ROLES.EVENT_ORGANIZER,
                },
              ].map(proRegistrationPersona => {
                return (
                  <option
                    key={proRegistrationPersona.value}
                    value={proRegistrationPersona.value}
                  >
                    {proRegistrationPersona.label}
                  </option>
                );
              })}
            </Select>
            {isWaldoProFreeRegister && (
              <div className="text-right text-xs mb-2">
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  href={`${marcomUrl}/org/?utm_source=Hero&utm_medium=Corporate+Non-profit&utm_campaign=Home+Page`}
                >
                  Not a pro? Click here
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {errorList?.length > 0 && (
        <ul className="mb-2 text-danger">
          {errorList.map(error => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      <p className="text-sm text-italic text-center">
        By continuing you agree to the{' '}
        <a href="/terms" target="_blank" rel="nofollow">
          Terms
        </a>{' '}
        &{' '}
        <a href="/terms" target="_blank" rel="nofollow">
          Privacy Policy
        </a>
      </p>

      <div className="text-center">
        <Button type="submit" disabled={loading} loading={loading}>
          {}
          {loading
            ? 'Processing...'
            : requiresCreditCard
              ? 'Continue'
              : 'Get Started Now'}
        </Button>
      </div>

      {!requiresCreditCard && (
        <p className="text-center mt-1 mb-0">
          <span className="d-block text-sm">No credit card required</span>
        </p>
      )}
    </form>
  );
};

RegisterViewDetailsForm.propTypes = propTypes;

export default RegisterViewDetailsForm;
